import React, { useCallback } from 'react';
import { Group, Text, Image, FolderMusic, IconButton } from '@landr/maestro';
import { css } from '@landr/styled-system/css';
import { plural, t } from '@lingui/macro';
import { useSelectedSamplesContext } from 'contexts/SelectedSamplesContext/SelectedSamplesContext';
import {
    UserCollection,
    Sample,
    AddSamplesToUserCollectionMutation,
    AddSamplesToUserCollectionMutationVariables,
} from 'types/generated/graphql';
import { useMutation, useApolloClient, gql } from '@apollo/client';
import {
    AddSamplesToUserCollection,
    UserCollection as UserCollectionGql,
} from 'apollo/userCollection/userCollection.gql';
import { QueryConfig } from 'apollo/userCollection/queryConfig';
import { useNotificationsContext } from 'contexts/NotificationsContext/NotificationsContext';
import { evictCacheByKeys } from 'helpers';
import {
    AddedMultipleSamplesToCollectionError,
    AddedMultipleSamplesToCollectionSuccess,
    AddedSingleSampleToCollectionError,
    AddedSingleSampleToCollectionSuccess,
} from 'contexts/NotificationsContext/notifications';
import { getImageUrl } from '../../helpers';

export interface CollectionsListItemProps {
    item: UserCollection;
    sampleIds?: string[];
    hide: () => void;
}

const useGetSampleFragment = (sampleId?: string) => {
    const apolloClient = useApolloClient();

    if (!sampleId) {
        return undefined;
    }

    const sampleFragment: Partial<Sample> | null = apolloClient.readFragment({
        id: `Sample:{"guid":"${sampleId}"}`,
        fragment: gql`
            fragment SampleFromGuid on Sample {
                guid
                name
            }
        `,
    });

    return sampleFragment?.name;
};

export const CollectionsListItem: React.FC<CollectionsListItemProps> = ({
    item,
    sampleIds,
    hide,
}) => {
    const { clearSamplesSelection } = useSelectedSamplesContext();
    const { createNotification } = useNotificationsContext();

    const sampleName = useGetSampleFragment(sampleIds && sampleIds[0]);

    const [requestAddSamplesToUserCollection] = useMutation<
        AddSamplesToUserCollectionMutation,
        AddSamplesToUserCollectionMutationVariables
    >(AddSamplesToUserCollection, {
        variables: { collectionGuid: item.guid, sampleIds },
        onCompleted: () => {
            if (!sampleIds) {
                return;
            }

            if (sampleIds.length > 1) {
                createNotification(
                    new AddedMultipleSamplesToCollectionSuccess(
                        item.name,
                        sampleIds.length,
                    ),
                );
            } else {
                createNotification(
                    new AddedSingleSampleToCollectionSuccess(
                        item.name,
                        sampleName,
                    ),
                );
            }
        },
        onError: () => {
            if (!sampleIds) {
                return;
            }

            if (sampleIds.length > 1) {
                createNotification(
                    new AddedMultipleSamplesToCollectionError(
                        item.name,
                        sampleIds.length,
                    ),
                );
            } else {
                createNotification(
                    new AddedSingleSampleToCollectionError(
                        item.name,
                        sampleName,
                    ),
                );
            }
        },
        update: (cache) => {
            evictCacheByKeys(cache, [
                item?.guid,
                QueryConfig.pathToItems,
                'userCollectionsFeed',
            ]);
        },
        // refetch is needed to get new generated artwork
        refetchQueries: [
            {
                query: UserCollectionGql,
                variables: { collectionGuid: item.guid },
                fetchPolicy: 'network-only',
            },
        ],
    });

    const onCardClick = useCallback(async () => {
        if (!sampleIds) {
            return;
        }

        await requestAddSamplesToUserCollection();

        clearSamplesSelection();
        hide();
    }, [
        sampleIds,
        clearSamplesSelection,
        hide,
        requestAddSamplesToUserCollection,
    ]);

    const packIconStyles = css({
        position: 'relative',
        width: { base: 'md', md: '28px' },
        height: { base: 'md', md: '28px' },
        borderRadius: 'sm',
        color: 'text.base',
    });

    const nameAndMobileDetailsStyles = css({
        display: 'flex',
        flexDirection: 'column',
        alignContent: 'center',
        justifyContent: 'flex-start',
        flexWrap: 'wrap',
        fontWeight: { sm: '400' },
    });

    const nameStyles = css({
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        width: '232px',
        textAlign: 'left',
        color: 'text.base',
    });

    const countStyles = css({
        lineHeight: '16px',
    });

    return (
        <Group alignItems="center" onClick={onCardClick} display="flex">
            <Group.Item marginLeft="xs">
                <Image
                    className={packIconStyles}
                    src={getImageUrl({ imageUrl: item.artworkUrl, width: 36 })}
                    loading="lazy"
                    fallback={() => (
                        <IconButton
                            icon={FolderMusic}
                            kind="link"
                            label={t`Artwork`}
                            isDisabled={true}
                            size="lg"
                        />
                    )}
                />
            </Group.Item>
            <Group.Item>
                <div className={nameAndMobileDetailsStyles}>
                    <Text
                        className={nameStyles}
                        textStyle="body"
                        mb="0"
                        fontWeight="normal"
                        pl="0"
                        marginLeft="0"
                    >
                        {item.name}
                    </Text>
                    {!!item?.sampleCount && (
                        <Text
                            className={countStyles}
                            color="text.medium"
                            textStyle="body-sm"
                        >
                            {plural(item.sampleCount, {
                                one: '# sample',
                                other: '# samples',
                            })}
                        </Text>
                    )}
                </div>
            </Group.Item>
        </Group>
    );
};
